import { Tooltip } from "antd-v5";
import Vocabulary from "components/Vocabulary";
import {
  ColumnOrderChangeCallback,
  ColumnOrderConfig,
  ColumnVisibilityChangeCallback,
} from "hooks/useTableCustomizationHelper";
import { includes, isBoolean, isFunction, noop } from "lodash";
import React from "react";
import { DateFormatType, DateFormats, DateFormatter } from "types/common";
import {
  GroupByChangeCallback,
  SortChangeCallBack,
  SortOrder,
  SortType,
} from "types/pagination";

import ACDropdown, {
  ACDropdownOptionProps,
  ACDropdownProps,
} from "ac-components/ACDropdown";
import ACButton from "ac-components/ac-button/ACButton";
import CalendarIcon from "ac-components/ac-icons/CalendarIcon";
import CheckIcon from "ac-components/ac-icons/CheckIcon";
import CloseIcon from "ac-components/ac-icons/CloseIcon";
import EllipsisVerticalIcon from "ac-components/ac-icons/EllipsisVerticalIcon";
import FilterIcon from "ac-components/ac-icons/FilterIcon";
import GroupingIcon from "ac-components/ac-icons/GroupingIcon";
import MoveLeftIcon from "ac-components/ac-icons/MoveLeftIcon";
import MoveRightIcon from "ac-components/ac-icons/MoveRightIcon";
import SortAscIcon from "ac-components/ac-icons/SortAscIcon";
import SortDescIcon from "ac-components/ac-icons/SortDescIcon";

type ReasonObject = { disabled: boolean; reason: string };

export type ACColumnOptionDropdownProps = {
  columnKey: string;
  sortOrder: SortOrder;
  hasSort: boolean;
  sortType?: SortType;
  onSortChange?: SortChangeCallBack;
  showFilter?: (key: string) => void;
  draggable?: boolean;
  columnKeys?: ColumnOrderConfig;
  columnOrderConfig?: ColumnOrderConfig;
  onColumnOrderChange?: ColumnOrderChangeCallback;
  onColumnVisibilityChange?: ColumnVisibilityChangeCallback;
  alwaysVisibleColumns?: React.Key[];
  onPopupVisibleChange: ACDropdownProps["onPopupVisibleChange"];
  dateFormatter?: DateFormatter;
  allowGroupBy?: boolean | ((key: string) => ReasonObject);
  groupByKey?: string;
  groupBy?: string[];
  onGroupByChange?: GroupByChangeCallback;
  showDropdown?: boolean;
};

const ACColumnOptionDropdown = ({
  columnKey,
  sortOrder,
  hasSort,
  sortType = "string",
  onSortChange,
  showFilter,
  draggable,
  columnKeys,
  columnOrderConfig,
  onColumnOrderChange,
  onColumnVisibilityChange,
  alwaysVisibleColumns,
  onPopupVisibleChange = noop,
  dateFormatter,
  allowGroupBy,
  groupByKey,
  groupBy,
  onGroupByChange,
  showDropdown = true,
}: ACColumnOptionDropdownProps) => {
  const getCheckIcon = (value: boolean) => (value ? <CheckIcon /> : undefined);
  const sortOptions: ACDropdownOptionProps[] = [];
  const otherOptions: ACDropdownOptionProps[] = [];

  if (!showDropdown) {
    return null;
  }

  if (sortType) {
    sortOptions.push(
      {
        label: <>Sort {SortTypeString[sortType].ascend}</>,
        value: "ascend",
        icon: <SortAscIcon />,
        secondaryIcon: getCheckIcon(sortOrder === "ascend"),
        disabled: !hasSort,
      },
      {
        label: <>Sort {SortTypeString[sortType].descend}</>,
        value: "descend",
        icon: <SortDescIcon />,
        secondaryIcon: getCheckIcon(sortOrder === "descend"),
        disabled: !hasSort,
      },
    );
  }
  if (showFilter) {
    sortOptions.push({
      label: "Filters",
      value: "filters",
      icon: <FilterIcon />,
    });
  }
  if (onGroupByChange) {
    const _groupByKey = groupByKey || columnKey;
    const alreadyGrouped = includes(groupBy, _groupByKey);
    const limitReached = groupBy && groupBy.length >= 3;
    const _allowGrouBy = isFunction(allowGroupBy)
      ? allowGroupBy(_groupByKey)
      : allowGroupBy;
    const groupByNotAllowed = isBoolean(_allowGrouBy)
      ? !_allowGrouBy
      : (_allowGrouBy?.disabled ?? true);
    const disabled = groupByNotAllowed || alreadyGrouped || limitReached;
    sortOptions.push({
      label: (
        <Tooltip
          title={
            groupByNotAllowed
              ? (_allowGrouBy && (_allowGrouBy as ReasonObject).reason) ||
                "You can’t group by this column"
              : alreadyGrouped
                ? "Already grouped"
                : limitReached
                  ? "Grouping limit reached"
                  : ""
          }
        >
          Add to Group By
        </Tooltip>
      ),
      value: "groupBy",
      icon: <GroupingIcon />,
      disabled,
    });
  }

  if (dateFormatter) {
    const format = dateFormatter.getFormat(columnKey);
    otherOptions.push({
      label: "Date Format",
      value: "dateFormat",
      icon: <CalendarIcon />,
      subMenuWidth: 290,
      items: [
        {
          label: "Full",
          value: "fullDate",
          secondaryLabel: "2024/07/21 - 11:54AM",
        },
        {
          label: "Date",
          value: "date",
          secondaryLabel: "July 21, 2024",
        },
        {
          label: "Date & Time",
          value: "datetime",
          secondaryLabel: "July 21, 2024 - 11:54AM",
        },
        {
          label: "Relative Date",
          value: "relativeTime",
          secondaryLabel: "16 hours ago",
        },
      ].map((item) => ({
        ...item,
        secondaryIcon: format === item.value ? <CheckIcon /> : undefined,
      })),
    });
  }
  if (draggable) {
    const currentDraggableIndex = columnKeys?.indexOf(columnKey) ?? 0;
    otherOptions.push(
      {
        label: "Move Left",
        value: "moveLeft",
        icon: <MoveLeftIcon />,
        disabled: currentDraggableIndex === 0,
      },
      {
        label: "Move Right",
        value: "moveRight",
        icon: <MoveRightIcon />,
        disabled: currentDraggableIndex >= (columnKeys?.length ?? 1) - 1,
      },
    );
  }
  if (alwaysVisibleColumns && !alwaysVisibleColumns.includes(columnKey)) {
    otherOptions.push({
      label: "Remove Column",
      value: "remove",
      icon: <CloseIcon />,
    });
  }

  const options: ACDropdownOptionProps[] = [...sortOptions];
  if (sortOptions.length && otherOptions.length) {
    options.push({
      type: "divider",
    });
  }
  options.push(...otherOptions);

  if (options.length === 0) {
    return null;
  }

  const onOptionClick: ACDropdownProps["onOptionClick"] = (key) => {
    const keyString = key as string;
    const currentIndex = columnOrderConfig?.indexOf(columnKey) ?? 0;
    if (key === "filters") {
      showFilter && showFilter(columnKey);
    } else if (["ascend", "descend"].includes(keyString)) {
      onSortChange && onSortChange({ [columnKey]: key as SortOrder });
    } else if (keyString === "moveLeft") {
      onPopupVisibleChange(false);
      onColumnOrderChange &&
        onColumnOrderChange(currentIndex, currentIndex - 1);
    } else if (keyString === "moveRight") {
      onPopupVisibleChange(false);
      onColumnOrderChange &&
        onColumnOrderChange(currentIndex, currentIndex + 1);
    } else if (keyString === "remove") {
      onColumnVisibilityChange && onColumnVisibilityChange(columnKey, false);
    } else if (keyString === "groupBy") {
      onGroupByChange &&
        onGroupByChange([...(groupBy || []), groupByKey || columnKey]);
    } else if (
      DateFormats.includes(keyString as DateFormatType) &&
      dateFormatter
    ) {
      dateFormatter.setFormat(columnKey, keyString as DateFormatType);
    }
  };

  return (
    <ACDropdown
      items={options}
      placement="bottomRight"
      onOptionClick={onOptionClick}
      offsetY={10}
      onPopupVisibleChange={onPopupVisibleChange}
      menuWidth={["date", "severity"].includes(sortType) ? undefined : 195}
    >
      <ACButton
        size="sm"
        type="ghost"
        iconOnly
        className="ac-table-column-filter-sorter-group"
        onClick={(e) => e.stopPropagation()}
      >
        <EllipsisVerticalIcon />
      </ACButton>
    </ACDropdown>
  );
};

export default ACColumnOptionDropdown;

const SortTypeString: Record<
  SortType,
  Record<"ascend" | "descend", React.ReactNode>
> = {
  date: {
    ascend: "Earliest-Latest",
    descend: "Latest-Earliest",
  },
  number: {
    ascend: "0-9",
    descend: "9-0",
  },
  string: {
    ascend: "A-Z",
    descend: "Z-A",
  },
  severity: {
    ascend: (
      <>
        <Vocabulary name="critical" /> last
      </>
    ),
    descend: (
      <>
        <Vocabulary name="critical" /> first
      </>
    ),
  },
};
